// Lightweight localStorage-backed workspace: history, free-check quota, license.
const HISTORY_KEY = "lc_history";
const FREE_KEY = "lc_free_used";
const LICENSE_KEY = "lc_license_email";
const MAX_HISTORY = 30;

export function getHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
}

export function saveToHistory(entry) {
  const list = getHistory();
  const item = {
    id: entry.id || `${Date.now()}`,
    created_at: new Date().toISOString(),
    lc_terms: entry.lc_terms,
    documents: entry.documents,
    result: entry.result,
    title: entry.title || `Check · ${new Date().toLocaleString()}`,
  };
  const next = [item, ...list.filter((x) => x.id !== item.id)].slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return item;
}

export function deleteHistory(id) {
  const next = getHistory().filter((x) => x.id !== id);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
}

export function clearHistory() {
  localStorage.removeItem(HISTORY_KEY);
}

// ---- saved amendment letters ----
const AMEND_KEY = "lc_amendments";
const MAX_AMEND = 30;

export function getAmendments() {
  try {
    return JSON.parse(localStorage.getItem(AMEND_KEY) || "[]");
  } catch {
    return [];
  }
}

export function saveAmendment(entry) {
  const list = getAmendments();
  const item = {
    id: entry.id || `am_${Date.now()}`,
    created_at: new Date().toISOString(),
    subject: entry.subject || "",
    letter_body: entry.letter_body || "",
    requested_amendments: entry.requested_amendments || [],
    lang: entry.lang || "en",
  };
  const next = [item, ...list.filter((x) => x.id !== item.id)].slice(0, MAX_AMEND);
  localStorage.setItem(AMEND_KEY, JSON.stringify(next));
  return item;
}

export function deleteAmendment(id) {
  const next = getAmendments().filter((x) => x.id !== id);
  localStorage.setItem(AMEND_KEY, JSON.stringify(next));
  return next;
}

// ---- issuing-bank details (reused across amendment letters) ----
const BANK_KEY = "lc_bank_info";
export function getBankInfo() {
  try {
    return { name: "", address: "", email: "", ...JSON.parse(localStorage.getItem(BANK_KEY) || "{}") };
  } catch {
    return { name: "", address: "", email: "" };
  }
}
export function setBankInfo(info) {
  localStorage.setItem(BANK_KEY, JSON.stringify({ name: info.name || "", address: info.address || "", email: info.email || "" }));
}

// ---- free-check quota ----
export function getFreeUsed() {
  return parseInt(localStorage.getItem(FREE_KEY) || "0", 10);
}
export function incFreeUsed() {
  const n = getFreeUsed() + 1;
  localStorage.setItem(FREE_KEY, String(n));
  return n;
}

// ---- license (email unlock) ----
export function getLicenseEmail() {
  return localStorage.getItem(LICENSE_KEY) || "";
}
export function setLicenseEmail(email) {
  if (email) localStorage.setItem(LICENSE_KEY, email);
  else localStorage.removeItem(LICENSE_KEY);
}

// ---- team invite code ----
const INVITE_KEY = "lc_invite_code";
export function getInviteCode() {
  return localStorage.getItem(INVITE_KEY) || "";
}
export function setInviteCode(code) {
  if (code) localStorage.setItem(INVITE_KEY, code.toUpperCase());
  else localStorage.removeItem(INVITE_KEY);
}

// ---- onboarding ----
const ONBOARD_KEY = "lc_onboarded";
export function getOnboarded() {
  return localStorage.getItem(ONBOARD_KEY) === "1";
}
export function setOnboarded(v) {
  if (v) localStorage.setItem(ONBOARD_KEY, "1");
  else localStorage.removeItem(ONBOARD_KEY);
}

// ---- one free real audit credit (own data) ----
const FREE_CREDIT_KEY = "lc_free_credit_used";
export function getFreeCreditUsed() {
  return localStorage.getItem(FREE_CREDIT_KEY) === "true";
}
export function setFreeCreditUsed(v) {
  if (v) localStorage.setItem(FREE_CREDIT_KEY, "true");
  else localStorage.removeItem(FREE_CREDIT_KEY);
}

// ---- interactive guided walkthrough (spotlight tour) ----
// Primary key is `tour_completed`; legacy `lc_onboarding_completed` still honored so
// users who already finished the tour won't see it replay.
const TOUR_KEY = "tour_completed";
const TOUR_KEY_LEGACY = "lc_onboarding_completed";
export function getOnboardingCompleted() {
  return localStorage.getItem(TOUR_KEY) === "true" || localStorage.getItem(TOUR_KEY_LEGACY) === "true";
}
export function setOnboardingCompleted(v) {
  if (v) { localStorage.setItem(TOUR_KEY, "true"); localStorage.setItem(TOUR_KEY_LEGACY, "true"); }
  else { localStorage.removeItem(TOUR_KEY); localStorage.removeItem(TOUR_KEY_LEGACY); }
}
